import { useFlags } from "launchdarkly-react-client-sdk";
import { Icon } from "../../../components/Icon/Icon.tsx";
import { Spinner } from "../../../components/Spinner/Spinner.tsx";
import { useImageDropZone } from "../../../hooks/useImageDropZone.ts";
import { usePreserveSearchParamsNavigate } from "../../../hooks/usePreserveSearchParamsNavigate.ts";
import {
  BOARDS,
  BRIEF_TOOL,
  EDIT_WORKSPACE,
  GENERATE_TOOL,
  TRANSFER_TOOL,
  WORKSPACES,
} from "../../../routes.ts";
import { selectedAssetUuidStore } from "../../boardState/stores/selectedAssetUuidStore.ts";
import { UPLOAD_MAX_IMAGE_SIZE } from "../../constants.ts";
import { useWorkspaceUuid } from "../../hooks/useWorkspaceUuid.ts";
import { ToolbarActionItem } from "../components/Toolbar/ToolbarActionItem.tsx";
import { ToolbarActionItemTooltipContent } from "../components/Toolbar/ToolbarActionItemTooltipContent.tsx";
import { useAddImageOnBoard } from "../hooks/useAddImageOnBoard.ts";
import { useBoardUuid } from "../hooks/useBoardUuid.ts";
import { useSelectedTool } from "../hooks/useSelectedTool.ts";

export const CreateToolsToolbar = () => {
  const { selectedTool, setSelectedTool } = useSelectedTool();
  const flags = useFlags();
  return (
    <div className="flex-col items-center h-full w-full overflow-y-auto overflow-x-clip scrollbar-hide">
      <ToolbarActionItem
        onClick={() =>
          setSelectedTool(
            selectedTool === GENERATE_TOOL ? undefined : GENERATE_TOOL,
          )
        }
        isActive={selectedTool === GENERATE_TOOL}
        title="Generate"
        tooltipContent={
          <ToolbarActionItemTooltipContent
            title="Generate"
            description="Prompt and generate"
            beforeImageUrl="https://storage.googleapis.com/419c45cf-be8a-4cba-bbcd-74a221eb2587/app/assets/navbar/tools/create_1_v2.png"
            afterImageUrl="https://storage.googleapis.com/419c45cf-be8a-4cba-bbcd-74a221eb2587/app/assets/navbar/tools/create_2.png"
          />
        }
      >
        <Icon name="Sparkles" size="sm" />
      </ToolbarActionItem>
      <ToolbarActionItem
        onClick={() =>
          setSelectedTool(selectedTool === BRIEF_TOOL ? undefined : BRIEF_TOOL)
        }
        isActive={selectedTool === BRIEF_TOOL}
        title="Brief"
        tooltipContent={
          <ToolbarActionItemTooltipContent
            title="Brief"
            description="Generate images from a campaign brief"
            beforeImageUrl="https://storage.googleapis.com/419c45cf-be8a-4cba-bbcd-74a221eb2587/app/assets/navbar/tools/brief_1.png"
            afterImageUrl="https://storage.googleapis.com/419c45cf-be8a-4cba-bbcd-74a221eb2587/app/assets/navbar/tools/brief_2.png"
          />
        }
      >
        <Icon name="LayoutDashboard" size="sm" />
      </ToolbarActionItem>
      {flags["styleTransfer"] && (
        <ToolbarActionItem
          onClick={() =>
            setSelectedTool(
              selectedTool === TRANSFER_TOOL ? undefined : TRANSFER_TOOL,
            )
          }
          isActive={selectedTool === TRANSFER_TOOL}
          title="Transfer"
          tooltipContent={
            <ToolbarActionItemTooltipContent
              title="Transfer style"
              description="Apply your style to your image"
              beforeImageUrl="https://storage.googleapis.com/419c45cf-be8a-4cba-bbcd-74a221eb2587/app/assets/navbar/tools/transfer_1.png"
              afterImageUrl="https://storage.googleapis.com/419c45cf-be8a-4cba-bbcd-74a221eb2587/app/assets/navbar/tools/transfer_2.png"
            />
          }
        >
          <Icon name="ArrowLeftRight" size="sm" />
        </ToolbarActionItem>
      )}
      <UploadButton />
    </div>
  );
};

const UploadButton = () => {
  const workspaceUuid = useWorkspaceUuid();
  const boardUuid = useBoardUuid();
  const navigate = usePreserveSearchParamsNavigate();
  const { mutate: uploadImage, isLoading: isUploadLoading } =
    useAddImageOnBoard({
      onUploaded: (assetUuid: string) => {
        selectedAssetUuidStore.setSelectedAssetUuid(assetUuid);
        navigate(
          `${WORKSPACES}/${workspaceUuid}/${BOARDS}/${boardUuid}/${EDIT_WORKSPACE}`,
        );
      },
      maxSize: UPLOAD_MAX_IMAGE_SIZE,
    });
  const { getInputProps, open: openFileBrowser } = useImageDropZone({
    multiple: false,
    minImageSize: 256,
    onImageValidated: (file) => {
      if (file) {
        uploadImage({
          image: file.fileToUpload,
        });
      }
    },
  });
  return (
    <ToolbarActionItem
      onClick={openFileBrowser}
      isActive={false}
      title="Upload"
      tooltipContent={
        <div className="label-lg-semibold text-white">
          Upload an image from your computer
        </div>
      }
    >
      <>
        <input {...getInputProps()} />
        {isUploadLoading ? (
          <Spinner size="sm" />
        ) : (
          <Icon name="Upload" size="sm" />
        )}
      </>
    </ToolbarActionItem>
  );
};
